import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle2, 
  Trash2, 
  ArrowRight,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Clock
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Column mappings - simplified like reference image
const COLUMN_MAPPINGS = [
  { from: 'WEBSITE', to: 'website' },
  { from: 'COUNTRY', to: 'country' },
  { from: 'TOPIC', to: 'category' },
  { from: 'CASINO', to: 'casino' },
  { from: 'CBD', to: 'cbd' },
  { from: 'GAMBLING', to: 'gambling' },
  { from: 'TAG LABEL', to: 'tag_label' },
  { from: 'PVP', to: 'pvp' },
  { from: 'PC', to: 'pc' },
  { from: 'DR', to: 'dr' },
  { from: 'Visita Ahred', to: 'organic_traffic' },
  { from: 'LANGUAGE', to: 'language' },
  { from: 'PERIODICO, BLOG', to: 'site_type' },
  { from: 'DF/NF/SP', to: 'link_type' },
  { from: 'PREMIUM PRICE', to: 'premium_sale_price' },
  { from: 'duration_link', to: 'link_duration' },
];

const REMOVED_COLUMNS = [
  'DA', 'PRECIO COMPRA OFERTA', 'PVP OFERTA', 'FECHA INICIO OFERTA', 
  'FECHA FIN OFERTA', 'MORE INFO', 'INFO PRICE', 'CONTINENT', 
  'FECHA CREACION', 'FECHA CAMBIO', 'SEMANA', 'PERMITE REDACCION',
  'VALIDADO x ADMINISTRADOR', 'META', 'hsn_sac_code'
];

export const ExcelToolsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const cleanFileRef = useRef(null);
  const changesFileRef = useRef(null);
  
  const [cleanFile, setCleanFile] = useState(null);
  const [changesFile, setChangesFile] = useState(null);
  const [cleanResult, setCleanResult] = useState(null);
  const [changesResult, setChangesResult] = useState(null);
  const [loadingClean, setLoadingClean] = useState(false);
  const [loadingChanges, setLoadingChanges] = useState(false);

  const handleCleanFile = async () => {
    if (!cleanFile) {
      toast.error('Selecciona un archivo primero');
      return;
    }

    setLoadingClean(true);
    try {
      const formData = new FormData();
      formData.append('file', cleanFile);

      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/products/clean-excel`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob',
        timeout: 180000, // 3 minutes timeout
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      setCleanResult({ url, filename: `productos_limpio_${Date.now()}.xlsx` });
      toast.success('¡Excel procesado correctamente!');
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('El proceso está tardando demasiado. Intenta con un archivo más pequeño.');
      } else {
        toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
      }
    } finally {
      setLoadingClean(false);
    }
  };

  const handleDetectChanges = async () => {
    if (!changesFile) {
      toast.error('Selecciona un archivo primero');
      return;
    }

    setLoadingChanges(true);
    try {
      const formData = new FormData();
      formData.append('file', changesFile);

      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/products/detect-changes`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob',
        timeout: 180000, // 3 minutes timeout
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      setChangesResult({ url, filename: `cambios_detectados_${Date.now()}.xlsx` });
      toast.success('¡Análisis completado!');
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('El análisis está tardando demasiado. Intenta con un archivo más pequeño.');
      } else {
        toast.error(error.response?.data?.detail || 'Error al analizar el archivo');
      }
    } finally {
      setLoadingChanges(false);
    }
  };

  const downloadFile = (result) => {
    const link = document.createElement('a');
    link.href = result.url;
    link.setAttribute('download', result.filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-4 text-slate-600 hover:text-slate-800"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Admin
        </Button>
      </div>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Section 1: Clean Excel - Similar to reference */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            Limpiador de Catálogo Excel
          </h1>
          <p className="text-slate-500 mb-6">
            Sube tu catálogo de productos y obtén un Excel limpio con solo las columnas necesarias para la app
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Upload Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Upload className="h-4 w-4 text-slate-500" />
                  Subir Catálogo
                </CardTitle>
                <CardDescription className="text-sm">
                  Selecciona el archivo Excel con tu catálogo de productos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={cleanFileRef}
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    setCleanFile(e.target.files?.[0] || null);
                    setCleanResult(null);
                  }}
                />
                <div 
                  onClick={() => cleanFileRef.current?.click()}
                  data-testid="clean-excel-upload-area"
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-rose-300 hover:bg-rose-50/30 transition-all"
                >
                  <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  {cleanFile ? (
                    <p className="font-medium text-slate-700 text-sm">{cleanFile.name}</p>
                  ) : (
                    <>
                      <p className="font-medium text-slate-600 text-sm">Click para seleccionar archivo</p>
                      <p className="text-xs text-slate-400 mt-1">Solo archivos .xlsx o .xls</p>
                    </>
                  )}
                </div>

                <Button 
                  onClick={handleCleanFile}
                  disabled={!cleanFile || loadingClean}
                  data-testid="clean-excel-btn"
                  className="w-full mt-4 bg-rose-400 hover:bg-rose-500 text-white"
                >
                  {loadingClean ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  Limpiar Excel
                </Button>
              </CardContent>
            </Card>

            {/* Result Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Download className="h-4 w-4 text-slate-500" />
                  Resultado
                </CardTitle>
                <CardDescription className="text-sm">
                  Descarga el archivo limpio y los sitios modificados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50/50">
                  {cleanResult ? (
                    <>
                      <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-2" />
                      <p className="font-medium text-green-700 text-sm mb-3">¡Archivo listo!</p>
                      <Button 
                        onClick={() => downloadFile(cleanResult)}
                        data-testid="download-clean-excel-btn"
                        className="bg-green-500 hover:bg-green-600 text-white"
                        size="sm"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Descargar Excel Limpio
                      </Button>
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-400 text-sm">Sube un archivo para ver el resultado</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column Mappings - Simplified like reference */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Columns Kept */}
            <Card className="border-green-100 bg-green-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-green-700 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Columnas que se mantienen ({COLUMN_MAPPINGS.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {COLUMN_MAPPINGS.map((col, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-xs py-0.5">
                      <span className="text-slate-700 font-medium">{col.from}</span>
                      <ArrowRight className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span className="text-green-600">{col.to}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Columns Removed */}
            <Card className="border-red-100 bg-red-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-red-700 text-sm font-medium">
                  <Trash2 className="h-4 w-4" />
                  Columnas que se eliminan ({REMOVED_COLUMNS.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {REMOVED_COLUMNS.map((col, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white text-red-600 border-red-200 text-xs font-normal">
                      {col}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section 2: Detect Changes */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Clock className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                Detectar Cambios (72 horas)
              </h2>
              <p className="text-slate-500 text-sm">
                Compara un Excel con los productos actuales y detecta qué ha cambiado en las últimas 72 horas
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {/* Upload Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Upload className="h-4 w-4 text-slate-500" />
                  Subir Excel para Comparar
                </CardTitle>
                <CardDescription className="text-sm">
                  Selecciona el archivo Excel que quieres comparar con la base de datos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={changesFileRef}
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    setChangesFile(e.target.files?.[0] || null);
                    setChangesResult(null);
                  }}
                />
                <div 
                  onClick={() => changesFileRef.current?.click()}
                  data-testid="detect-changes-upload-area"
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-300 hover:bg-cyan-50/30 transition-all"
                >
                  <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  {changesFile ? (
                    <p className="font-medium text-slate-700 text-sm">{changesFile.name}</p>
                  ) : (
                    <>
                      <p className="font-medium text-slate-600 text-sm">Click para seleccionar archivo</p>
                      <p className="text-xs text-slate-400 mt-1">Solo archivos .xlsx o .xls</p>
                    </>
                  )}
                </div>

                <Button 
                  onClick={handleDetectChanges}
                  disabled={!changesFile || loadingChanges}
                  data-testid="detect-changes-btn"
                  className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  {loadingChanges ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Detectar Cambios
                </Button>
              </CardContent>
            </Card>

            {/* Result Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Download className="h-4 w-4 text-slate-500" />
                  Reporte de Cambios
                </CardTitle>
                <CardDescription className="text-sm">
                  Descarga el análisis con productos nuevos, eliminados y modificados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50/50">
                  {changesResult ? (
                    <>
                      <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-2" />
                      <p className="font-medium text-green-700 text-sm mb-3">¡Análisis completado!</p>
                      <Button 
                        onClick={() => downloadFile(changesResult)}
                        data-testid="download-changes-report-btn"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                        size="sm"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Descargar Reporte
                      </Button>
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-400 text-sm">Sube un archivo para ver los cambios</p>
                    </>
                  )}
                </div>

                {/* Legend */}
                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="bg-green-100 text-green-700 border-0 text-xs">Nuevos</Badge>
                    <span className="text-slate-500">Productos en Excel que no están en la BD</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="bg-red-100 text-red-700 border-0 text-xs">Eliminados</Badge>
                    <span className="text-slate-500">Productos en BD que no están en el Excel</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Modificados</Badge>
                    <span className="text-slate-500">Cambios en las últimas 72 horas</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelToolsPage;
